import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { observer } from 'mobx-react-lite';
import Tooltip from '@mui/material/Tooltip';

import { getAPIUrl } from '../api/apiUtils';
import ComparisonChart from '../components/ComparisonChart';
import { useGlobalFilters } from '../hooks/useGlobalFilters';
import { getGroupTitleById } from '../utils/groupTitles';
import './FrustrationIndicator.scss';

const PERIODS = [
  { value: 'day', label: 'По дням' },
  { value: 'week', label: 'По неделям' },
  { value: 'month', label: 'По месяцам' },
];

const CLASS_LABELS = {
  p: 'Фрустрация отсутствует',
  e: 'Экстрапунитивный, потребностно-неустойчивый',
  E: 'Экстрапунитивный, эго-защитный',
  "E'": 'Экстрапунитивный, препятственно-доминантный',
  f: 'Другой тип фрустрации',
};

const CLASS_DESCRIPTIONS = {
  p: 'Модель не обнаружила в предложении выраженной реакции на препятствие или неудовлетворённость.',
  e: 'Требование решения: автор настаивает или ожидает, что проблема будет устранена и нужный результат достигнут.',
  E: 'Внешнее обвинение: автор возлагает ответственность на других людей или обстоятельства, защищая свою позицию.',
  "E'": 'Акцент на препятствии: автор подчёркивает тяжесть, несправедливость или непреодолимость ситуации.',
  f: 'Фрустрация обнаружена, но модель не отнесла её уверенно к типам e, E или E\'.',
};

const CLASS_COLORS = {
  p: '#2e7d32',
  e: '#ef6c00',
  E: '#d32f2f',
  "E'": '#8e24aa',
  f: '#546e7a',
};

const FrustrationIndicator = observer(() => {
  const filters = useGlobalFilters();
  const [period, setPeriod] = useState('day');
  const [payload, setPayload] = useState({ data: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const selectedGroups = filters.selectedGroups.map(String);
    if (selectedGroups.length === 0) {
      setPayload({ data: [], summary: null });
      setLoading(false);
      setError('');
      return undefined;
    }

    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(
          `${getAPIUrl()}/detection/frustration_indicator`,
          {
            params: {
              group_ids: selectedGroups.join(','),
              start_date: filters.startDate,
              end_date: filters.endDate,
              period,
            },
            signal: controller.signal,
          }
        );
        setPayload(response.data || { data: [], summary: null });
      } catch (requestError) {
        if (requestError.code !== 'ERR_CANCELED') {
          setError(
            requestError.response?.data?.error ||
            'Не удалось загрузить индикатор фрустрации'
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [filters.filtersString, period]);

  const chartData = useMemo(() => {
    const byGroup = new Map();
    const dates = new Set();
    (payload.data || []).forEach((row) => {
      const groupId = String(row.group_id);
      dates.add(row.date);
      if (!byGroup.has(groupId)) byGroup.set(groupId, new Map());
      byGroup.get(groupId).set(row.date, row);
    });
    const orderedDates = Array.from(dates).sort();
    return Array.from(byGroup.entries()).map(([groupId, valuesByDate]) => ({
      marker: getGroupTitleById(groupId),
      data: orderedDates.map((date) => ({
        date,
        value: valuesByDate.has(date) ? valuesByDate.get(date).value : null,
        totalSentences: valuesByDate.get(date)?.total_sentences || 0,
        frustratedSentences: valuesByDate.get(date)?.frustrated_sentences || 0,
        breakdown: valuesByDate.get(date)?.breakdown || {},
      })),
    }));
  }, [payload.data]);

  const getTooltipLines = ({ label, point, value }) => {
    if (!point) return `${label}: нет данных`;
    const breakdown = point.breakdown || {};
    return [
      `${label}: ${Number(value).toFixed(1)}%`,
      `Проанализировано: ${point.totalSentences}`,
      `С фрустрацией: ${point.frustratedSentences}`,
      `p — без фрустрации: ${breakdown.p || 0}`,
      `e — потребностно-неустойчивый: ${breakdown.e || 0}`,
      `E — эго-защитный: ${breakdown.E || 0}`,
      `E' — препятственно-доминантный: ${breakdown["E'"] || 0}`,
      `f — другой тип: ${breakdown.f || 0}`,
    ];
  };

  const dataLength = Math.max(0, ...chartData.map((series) => series.data.length));
  const summary = payload.summary;
  const total = summary?.total_sentences || 0;

  return (
    <div className="frustration-indicator">
      <div className="frustration-indicator__toolbar">
        <div>
          <h2>Уровень фрустрации</h2>
          <p>
            Доля предложений, в которых модель обнаружила любой тип фрустрации.
          </p>
        </div>
        <label>
          Агрегация
          <select value={period} onChange={(event) => setPeriod(event.target.value)}>
            {PERIODS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
      </div>

      {filters.selectedGroups.length === 0 ? (
        <div className="frustration-indicator__empty">
          Выберите сообщества в глобальном фильтре.
        </div>
      ) : loading ? (
        <div className="frustration-indicator__empty">Загрузка…</div>
      ) : error ? (
        <div className="frustration-indicator__error">{error}</div>
      ) : !summary || total === 0 ? (
        <div className="frustration-indicator__empty">
          За выбранный период данных фрустрационного анализа нет.
        </div>
      ) : (
        <>
          <div className="frustration-indicator__summary">
            <div className="frustration-indicator__main-value">
              <span>{summary.value}%</span>
              <small>эмоциональный индикатор</small>
            </div>
            <div className="frustration-indicator__stat">
              <strong>{summary.frustrated_sentences}</strong>
              <span>предложений с фрустрацией</span>
            </div>
            <div className="frustration-indicator__stat">
              <strong>{summary.total_sentences}</strong>
              <span>предложений проанализировано</span>
            </div>
          </div>

          <div className="frustration-indicator__breakdown">
            {Object.keys(CLASS_LABELS).map((className) => {
              const count = summary.breakdown?.[className] || 0;
              const percent = total ? (count * 100 / total).toFixed(1) : '0.0';
              return (
                <Tooltip
                  key={className}
                  title={CLASS_DESCRIPTIONS[className]}
                  placement="top"
                  enterDelay={250}
                  arrow
                  describeChild
                >
                  <div
                    className="frustration-indicator__class"
                    tabIndex={0}
                  >
                    <span
                      className="frustration-indicator__class-code"
                      style={{ backgroundColor: CLASS_COLORS[className] }}
                    >
                      {className}
                    </span>
                    <div>
                      <strong>{percent}%</strong>
                      <span>{CLASS_LABELS[className]}</span>
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>

          <div className="frustration-indicator__chart">
            <ComparisonChart
              loading={loading}
              chartData={chartData}
              dataWindow={{ start: 0, end: dataLength }}
              getTooltipLines={getTooltipLines}
              yAxisTitle="Доля фрустрации, %"
              yMin={0}
              yMax={100}
              formatYAxisTick={(value) => `${value}%`}
            />
          </div>
        </>
      )}
    </div>
  );
});

export default FrustrationIndicator;
